import useLatest from "ahooks/lib/useLatest";
import { isFunction, isNumber, isString } from "ahooks/lib/utils";
import { getTargetElement } from "ahooks/lib/utils/domTarget";
import useDeepCompareEffectWithTarget from "ahooks/lib/utils/useDeepCompareWithTarget";

// 键盘事件 keyCode 别名
const aliasKeyCodeMap = {
  0: 48,
  1: 49,
  2: 50,
  3: 51,
  4: 52,
  5: 53,
  6: 54,
  7: 55,
  8: 56,
  9: 57,
  backspace: 8,
  tab: 9,
  enter: 13,
  shift: 16,
  ctrl: 17,
  alt: 18,
  pausebreak: 19,
  capslock: 20,
  esc: 27,
  space: 32,
  pageup: 33,
  pagedown: 34,
  end: 35,
  home: 36,
  leftarrow: 37,
  uparrow: 38,
  rightarrow: 39,
  downarrow: 40,
  insert: 45,
  delete: 46,
  a: 65,
  b: 66,
  c: 67,
  d: 68,
  e: 69,
  f: 70,
  g: 71,
  h: 72,
  i: 73,
  j: 74,
  k: 75,
  l: 76,
  m: 77,
  n: 78,
  o: 79,
  p: 80,
  q: 81,
  r: 82,
  s: 83,
  t: 84,
  u: 85,
  v: 86,
  w: 87,
  x: 88,
  y: 89,
  z: 90,
  leftwindowkey: 91,
  rightwindowkey: 92,
  selectkey: 93,
  numpad0: 96,
  numpad1: 97,
  numpad2: 98,
  numpad3: 99,
  numpad4: 100,
  numpad5: 101,
  numpad6: 102,
  numpad7: 103,
  numpad8: 104,
  numpad9: 105,
  multiply: 106,
  add: 107,
  subtract: 109,
  decimalpoint: 110,
  divide: 111,
  f1: 112,
  f2: 113,
  f3: 114,
  f4: 115,
  f5: 116,
  f6: 117,
  f7: 118,
  f8: 119,
  f9: 120,
  f10: 121,
  f11: 122,
  f12: 123,
  numlock: 144,
  scrolllock: 145,
  semicolon: 186,
  equalsign: 187,
  comma: 188,
  dash: 189,
  period: 190,
  forwardslash: 191,
  graveaccent: 192,
  openbracket: 219,
  backslash: 220,
  closebracket: 221,
  singlequote: 222,
};

// 修饰键
const modifierKey = {
  ctrl: (event) => event.ctrlKey,
  shift: (event) => event.shiftKey,
  alt: (event) => event.altKey,
  meta: (event) => event.metaKey,
};

// 根据 event 计算激活键数量
function countKeyByEvent(event) {
  const countOfModifier = Object.keys(modifierKey).reduce((total, key) => {
    if (modifierKey[key](event)) {
      return total + 1;
    }

    return total;
  }, 0);

  // 16 17 18 91 92 是修饰键的 keyCode，如果 keyCode 是修饰键，那么激活数量就是修饰键的数量，如果不是，那么就需要 +1
  return [16, 17, 18, 91, 92].includes(event.keyCode)
    ? countOfModifier
    : countOfModifier + 1;
}

/**
 * 判断按键是否激活
 * @param [event]键盘事件
 * @param [keyFilter: any] 当前键
 * @returns Boolean
 */
function genFilterKey(event, keyFilter, exactMatch) {
  // 浏览器自动补全 input 的时候，会触发 keyDown、keyUp 事件，但此时 event.key 等为空
  if (!event.key) {
    return false;
  }

  // 数字类型直接匹配事件的 keyCode
  if (isNumber(keyFilter)) {
    return event.keyCode === keyFilter;
  }

  // 字符串依次判断是否有组合键
  const genArr = keyFilter.split(".");
  let genLen = 0;

  for (const key of genArr) {
    // 组合键
    const genModifier = modifierKey[key];
    // keyCode 别名
    const aliasKeyCode = aliasKeyCodeMap[key.toLowerCase()];

    if (
      (genModifier && genModifier(event)) ||
      (aliasKeyCode && aliasKeyCode === event.keyCode)
    ) {
      genLen++;
    }
  }

  /**
   * 需要判断触发的键位和监听的键位完全一致，判断方法就是触发的键位里有且等于监听的键位
   * genLen === genArr.length 能判断出来触发的键位里有监听的键位
   * countKeyByEvent(event) === genArr.length 判断出来触发的键位数量里有且等于监听的键位数量
   * 主要用来防止按组合键其子集也会触发的情况，例如监听 ctrl+a 会触发监听 ctrl 和 a 两个键的事件。
   */
  if (exactMatch) {
    return genLen === genArr.length && countKeyByEvent(event) === genArr.length;
  }
  return genLen === genArr.length;
}

/**
 * 键盘输入预处理方法
 * @param [keyFilter: any] 当前键
 * @returns () => Boolean
 */
function genKeyFormatter(keyFilter, exactMatch) {
  if (isFunction(keyFilter)) {
    return keyFilter;
  }
  if (isString(keyFilter) || isNumber(keyFilter)) {
    return (event) => genFilterKey(event, keyFilter, exactMatch);
  }
  if (Array.isArray(keyFilter)) {
    return (event) =>
      keyFilter.some((item) => genFilterKey(event, item, exactMatch));
  }
  return () => Boolean(keyFilter);
}

const defaultEvents = ["keydown"];

class GlobalEventShotcut {
  constructor() {
    if (typeof window === "undefined") {
      // eslint-disable-next-line no-undef
      this.el = globalThis;
    } else {
      this.el = window;
    }
    this.eventMap = {};
    this.eventTypes = {};
  }

  _bindEvent(eventName) {
    let event = this.eventTypes[eventName];

    if (!event) {
      event = (e) => {
        this.callHandler(eventName, e);
      };
      this.eventTypes[eventName] = event;
    }

    this.el.addEventListener(eventName, event);
  }

  _unbindEvent(eventName) {
    let event = this.eventTypes[eventName];
    this.el.removeEventListener(eventName, event);
    delete this.eventTypes[eventName];
  }

  getEventSortedHandlers(eventName) {
    return (this.eventMap[eventName] || []).sort((a, b) =>
      a.order - b.order > 0 ? -1 : 1
    );
  }

  callHandler(eventName, event) {
    const sortedHandlers = this.getEventSortedHandlers(eventName);
    sortedHandlers.every((item) => {
      const result = item.handler(event);
      return result !== false;
    });
  }

  addHandler(eventName, handler, options = {}) {
    if (!this.eventMap[eventName]) {
      this.eventMap[eventName] = [];
      this._bindEvent(eventName);
    }

    this.eventMap[eventName].push({
      order: options.order ?? 1,
      handler,
    });
  }

  deleteHandler(eventName, handler) {
    const handlers = this.eventMap[eventName] ?? [];
    if (handler) {
      // 删除特定函数
      handlers.some((item, index) => {
        if (item.handler === handler) {
          handlers.splice(index, 1);
          return true;
        }
        return false;
      });
    } else {
      handlers.length = 0;
    }

    if (!handlers.length) {
      this._unbindEvent(eventName);
      delete this.eventMap[eventName];
    }
  }
}

const globalEventShotcut = new GlobalEventShotcut();

/**
 * 快捷键，支持重复快捷键优执行优先级，快捷键冲突取消其他快捷键功能，参数参看ahooks的useKeypress功能
 * @param {array | string} keyFilter
 * @param {function} eventHandler 快捷键执行的方法，取消相同快捷键其他方法是函数返回false
 * @param {object} option
 * @param {object} option.order 相同快捷键执行顺序，越大优先级越高
 */
function useShortcut(keyFilter, eventHandler, option) {
  const {
    events = defaultEvents,
    target,
    exactMatch = false,
    // useCapture = false,
    order = 1, // 执行顺序，越大优先级越高
  } = option || {};
  const eventHandlerRef = useLatest(eventHandler);
  const keyFilterRef = useLatest(keyFilter);

  useDeepCompareEffectWithTarget(
    () => {
      const el = getTargetElement(target, window);
      if (!el) {
        return;
      }

      const callbackHandler = (event) => {
        const genGuard = genKeyFormatter(keyFilterRef.current, exactMatch);
        if (genGuard(event)) {
          const result = eventHandlerRef.current?.(event);
          return result;
        }
      };

      for (const eventName of events) {
        // el?.addEventListener?.(eventName, callbackHandler, useCapture);
        globalEventShotcut.addHandler(eventName, callbackHandler, { order });
      }
      return () => {
        for (const eventName of events) {
          // el?.removeEventListener?.(eventName, callbackHandler, useCapture);
          globalEventShotcut.deleteHandler(eventName, callbackHandler);
        }
      };
    },
    [events],
    target
  );
}

export default useShortcut;
